import { TRequiredKeys } from '@obvious.tech/constellation'
// used exclusively for deprecation annotation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { TEntityUntagged } from '@obvious.tech/constellation/Entity'
import { HttpError } from 'ra-core'
import { useCallback, useRef, useState } from 'react'

import { getOptions, sanitizeResource } from 'src/providers/dataProvider'
import { fetchJson } from 'src/providers/fetchJson'
import { TAwaited, TDeepReplace, TNonEmptyArray } from 'src/types'
import { cancellablePromise } from 'src/utils'
import { deepReplace } from '../deepReplace'
import { toDataURL } from '../file'
import Loadable, { TLoadable } from '../Loadable'
import { Filter, TFilterOpts, filterOptsToQueryString } from './Filter'
import { ResultAsync } from 'neverthrow'

/**
 * Because current constellation types have some "Date" types that are in
 * reality strings with this implementation
 *
 * @deprecated use {@link TEntityUntagged} instead
 */
export type TEntity<T> = TDeepReplace<
  T extends { id?: string } ? TRequiredKeys<T, 'id'> : T,
  Date,
  string
>

export type TApiError = Error | HttpError

export type TApiResponseSuccess<T> = {
  _tag: 'success'
  data: T
  error?: never
}

export type TApiResponseFailure<
  E extends TApiError | TNonEmptyArray<TApiError> = TApiError,
> = {
  _tag: 'failure'
  error: E
  data?: never
}

export type TApiResponse<
  T,
  E extends TApiError | TNonEmptyArray<TApiError> = TApiError,
> = TApiResponseSuccess<T> | TApiResponseFailure<E>

export type TApiAction<
  T = any,
  Options = any,
  E extends TApiError | TNonEmptyArray<TApiError> =
    | TApiError
    | TNonEmptyArray<TApiError>,
> = (options: Options) => Promise<TApiResponse<T, E>>

export const fetchResource =
  <T>(resource: string) =>
  async (_options: {
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT'
    body?: any
    headers?: Headers
    token?: string
  }): Promise<TApiResponse<T>> => {
    const { token, ...fetchOptions } = _options
    const options = {
      ...getOptions(token),
      ...fetchOptions,
    }

    try {
      const { json: data } = await fetchJson(
        `${window._env_.API_URL}/${sanitizeResource(resource)}`,
        options,
      )

      return {
        _tag: 'success',
        /** Because current constellation types are wrong
         * (API may return null values, but models don't show it)
         */
        data: deepReplace(
          null,
          undefined,
        )(data) as TApiResponseSuccess<T>['data'],
      }
    } catch (e) {
      return {
        _tag: 'failure',
        error: e instanceof Error ? e : new Error(String(e)),
      }
    }
  }

export const getResourceByID =
  <T>(resource: string) =>
  async ({
    id,
    token,
  }: {
    id: string | undefined
    token?: string
  }): Promise<TApiResponse<T>> => {
    return await fetchResource<T>(`${resource}/${id}`)({
      token,
    })
  }

export const getResourcesWithFilters =
  <T extends { id?: string }>(resource: string) =>
  async ({
    filters,
    token,
    sorts,
  }: {
    filters?: TFilterOpts<T>
    token?: string
    sorts?: string
  }): Promise<TApiResponse<T[]>> => {
    const filterQs = filterOptsToQueryString(filters)
    return await fetchResource<T[]>(
      sorts !== undefined
        ? `${resource}?Filters=${filterQs}&sorts=${sorts}`
        : `${resource}?Filters=${filterQs}`,
    )({
      token,
    })
  }

export const getResourceBlobAsDataURL =
  (resource: string) =>
  ({ token }: { token?: string }): ResultAsync<string, Error | HttpError> => {
    const options = {
      ...getOptions(token),
      token,
    }
    const url = `${window._env_.API_URL}/${sanitizeResource(resource)}`

    return ResultAsync.fromPromise(
      (async () => {
        const res = await fetch(url, options)

        if (!res.ok)
          throw new HttpError(
            res.statusText === '' ? `HTTP ${res.status}` : res.statusText,
            res.status,
            { url },
          )

        const blob = await res.blob()

        const dataURL = await toDataURL(blob)
        if (typeof dataURL !== 'string')
          throw new Error('Failed to convert blob to dataURL.')

        return dataURL
      })(),
      (e): Error => {
        return e instanceof Error
          ? e
          : new Error('An unexpected error occured', { cause: e })
      },
    )
  }

export type TUseApiActionStateData<T extends TApiAction> = Extract<
  TAwaited<ReturnType<T>>,
  { _tag: 'success' }
>['data']

export type TUseApiActionStateError<T extends TApiAction> = Extract<
  TAwaited<ReturnType<T>>,
  { _tag: 'failure' }
>['error']

export type TUseApiActionState<T extends TApiAction> = TLoadable<
  TUseApiActionStateData<T>,
  TUseApiActionStateError<T>
>

export const useApiAction = <
  T,
  O = any,
  E extends TApiError | TNonEmptyArray<TApiError> = TApiError,
>(
  action: TApiAction<T, O, E>,
): [
  TUseApiActionState<TApiAction<T, O, E>>,
  (opts: Parameters<typeof action>[0]) => () => void,
] => {
  const [state, setState] = useState<TUseApiActionState<TApiAction<T, O, E>>>(
    Loadable.idle(),
  )
  const cancelRef = useRef(() => {})

  const act = useCallback(
    (options: Parameters<typeof action>[0]): (() => void) => {
      cancelRef.current()

      setState(Loadable.loading())

      const promise = cancellablePromise(action(options))

      void (async () => {
        const response = await promise

        setState(() => {
          if (response._tag === 'failure')
            return Loadable.failure(response.error)
          return Loadable.success(response.data)
        })
      })()

      cancelRef.current = (reason?: unknown) => promise.cancel(reason)
      return cancelRef.current
    },
    [action],
  )

  return [state, act]
}

export default {
  fetchResource,
  getResourceByID,
  getResourcesWithFilters,
  useApiAction,
  getResourceBlobAsDataURL,
  Filter,
}
